// src/components/legal/TermsAcceptance.js
//
// The "I have read the Terms" checkbox, used at registration and at every
// payment entry point.
//
// ===========================================================================
// IS THE FORCED POPUP NECESSARY? — SHORT ANSWER: NO, AND IT MAY BE WORSE
// ===========================================================================
// You asked whether you need a modal people must scroll before they can tick
// the box. You don't, and forcing one tends to hurt you rather than help.
//
// What actually makes an agreement stick is whether a court would say the user
// had *reasonable notice* and took a *clear affirmative act* to agree. The
// pattern that has held up consistently is exactly this one:
//
//   - an UNCHECKED box (never pre-ticked)
//   - immediately beside the button that creates the account or takes payment
//   - with the words "I have read and agree to the Terms & Conditions"
//   - where "Terms & Conditions" is a real, working, clearly styled link
//   - and the record of acceptance is stored with a version and timestamp
//
// That's "clickwrap", and it's the strong form. What fails is "browsewrap" —
// a Terms link buried in the footer with no affirmative act. Your footer link
// alone is not enough, which is part of why this component exists.
//
// A forced-scroll modal adds friction at your highest-value moment for no
// extra enforceability, and it can backfire: if someone dismisses it without
// reading, you've created a record showing you knew they didn't read it. The
// modal below is therefore OPTIONAL and OFF by default — offered as a
// convenience for people who want to read without losing their place in the
// form, not as a gate.
//
// The one thing that is not optional: store the acceptance. A checkbox that
// gates a button but writes nothing to the database proves nothing three
// months later.

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { TERMS_VERSION } from "../../config/terms";

/**
 * @param {boolean}  checked
 * @param {Function} onChange     (bool) => void
 * @param {string}   context      "signup" | "checkout" | "trial"
 * @param {boolean}  showError    render the box in an error state
 * @param {boolean}  allowInlineRead  show a "Read here" link that opens the
 *                                    summary modal. Default true.
 */
export default function TermsAcceptance({
  checked,
  onChange,
  context = "signup",
  showError = false,
  allowInlineRead = true,
}) {
  const [readerOpen, setReaderOpen] = useState(false);

  const emphasis =
    context === "checkout" || context === "trial" ? (
      <>
        , including that <strong>all payments are non-refundable</strong> and
        that subscriptions renew automatically until cancelled
      </>
    ) : (
      <>
        , including that content may not be reproduced without written
        permission and that <strong>payments are non-refundable</strong>
      </>
    );

  return (
    <>
      <label
        style={{
          ...s.row,
          ...(showError && !checked ? s.rowError : null),
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          style={s.checkbox}
          aria-describedby="terms-acceptance-text"
        />
        <span id="terms-acceptance-text" style={s.text}>
          I have read and agree to the{" "}
          <Link to="/terms-of-service" target="_blank" rel="noopener noreferrer" style={s.link}>
            Terms &amp; Conditions
          </Link>{" "}
          and{" "}
          <Link to="/privacy-policy" target="_blank" rel="noopener noreferrer" style={s.link}>
            Privacy Policy
          </Link>
          {emphasis}.
          {allowInlineRead && (
            <>
              {" "}
              <button
                type="button"
                style={s.inlineBtn}
                onClick={(e) => {
                  e.preventDefault();
                  setReaderOpen(true);
                }}
              >
                Read a summary
              </button>
            </>
          )}
        </span>
      </label>

      {showError && !checked && (
        <p style={s.errorText}>
          Please confirm you've read the Terms &amp; Conditions to continue.
        </p>
      )}

      {readerOpen && <TermsSummaryModal onClose={() => setReaderOpen(false)} />}
    </>
  );
}

/**
 * A plain-language summary. Deliberately labelled as a summary, and it links
 * to the full Terms — a summary that pretends to be the agreement is worse
 * than no summary.
 */
function TermsSummaryModal({ onClose }) {
  return (
    <div style={s.overlay} onClick={onClose} role="presentation">
      <div
        style={s.modal}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Terms summary"
      >
        <div style={s.modalHead}>
          <h3 style={s.modalTitle}>The short version</h3>
          <button style={s.close} onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <p style={s.modalNote}>
          This is a plain-language summary, not the agreement itself. The full{" "}
          <Link to="/terms-of-service" target="_blank" style={s.link}>
            Terms &amp; Conditions
          </Link>{" "}
          are what apply.
        </p>

        <ul style={s.summaryList}>
          <li>
            <strong>Our material stays ours.</strong> Songs, videos, games and
            lessons are licensed to you for your own classroom or household.
            You may not download, copy, record, re-upload, or share them
            without our written permission.
          </li>
          <li>
            <strong>We can't guarantee accuracy.</strong> We work hard on our
            content and standards alignment, but we don't warrant that any
            topic, question, answer, or tool is correct or right for your
            curriculum. Review material before using it with students.
          </li>
          <li>
            <strong>Scores aren't grades.</strong> Progress stars and classroom
            reports are engagement signals, not assessments of ability.
          </li>
          <li>
            <strong>Payments are non-refundable.</strong> That's why we offer a
            free trial first — please use it to decide. Subscriptions renew
            automatically until you cancel, and you can cancel any time.
          </li>
          <li>
            <strong>Questions you write, we may use.</strong> If you submit
            quiz questions, you keep ownership but you give us permission to
            use and publish them as part of the product.
          </li>
          <li>
            <strong>Don't put student names in free-text fields.</strong> Quiz
            questions and uploads may be seen by others.
          </li>
        </ul>

        <button style={s.modalBtn} onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
}

const s = {
  row: {
    display: "flex",
    gap: 10,
    alignItems: "flex-start",
    padding: "12px 14px",
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    margin: "6px 0 14px",
    cursor: "pointer",
  },
  rowError: { borderColor: "#fca5a5", background: "#fef2f2" },
  checkbox: { marginTop: 3, width: 17, height: 17, flexShrink: 0, cursor: "pointer" },
  text: { fontSize: 13.5, lineHeight: 1.55, color: "#334155" },
  link: { color: "#1d4ed8", fontWeight: 600 },
  inlineBtn: {
    background: "none",
    border: "none",
    padding: 0,
    color: "#64748b",
    fontSize: 13,
    fontWeight: 600,
    textDecoration: "underline",
    cursor: "pointer",
  },
  errorText: {
    margin: "-8px 0 12px",
    color: "#b91c1c",
    fontSize: 13,
    fontWeight: 600,
  },
  overlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.6)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    zIndex: 1300,
  },
  modal: {
    background: "#fff",
    borderRadius: 14,
    padding: 26,
    width: "100%",
    maxWidth: 560,
    maxHeight: "85vh",
    overflowY: "auto",
  },
  modalHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  modalTitle: { margin: 0, fontSize: 20, fontWeight: 700, color: "#0f172a" },
  modalNote: { fontSize: 13, color: "#64748b", margin: "6px 0 14px" },
  summaryList: {
    paddingLeft: 20,
    margin: 0,
    fontSize: 14.5,
    lineHeight: 1.6,
    color: "#334155",
  },
  close: {
    background: "none",
    border: "none",
    fontSize: 26,
    lineHeight: 1,
    color: "#94a3b8",
    cursor: "pointer",
  },
  modalBtn: {
    marginTop: 20,
    width: "100%",
    background: "#0f172a",
    color: "#fff",
    border: "none",
    borderRadius: 10,
    padding: "12px 18px",
    fontWeight: 700,
    cursor: "pointer",
  },
};
